import React, { useCallback, useEffect, useMemo, useState } from "react";
import PatientSearch, { type PatientResult } from "../doctor/PatientSearch";
import ManagerPatientHistory from "./ManagerPatientHistory";
import { apiGet, apiPost } from "../../services/api";
import "./ManagerRecords.css";

interface SelectedPatient {
  patient_id: string | number;
  name?: string;
}

interface InvoiceRow {
  id: number;
  invoice_no?: string;
  invoice_date?: string;
  final_amount?: number;
  paid_total?: number;
  balance_due?: number;
  status?: string;
  [key: string]: unknown;
}

interface InvoicePaymentRow {
  id: number;
  kind?: string;
  amount?: number;
  method?: string;
  notes?: string;
  created_at?: string;
  [key: string]: unknown;
}

function PatientInvoiceLedger({ patientId }: { patientId: string }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [paymentModal, setPaymentModal] = useState<{
    invoiceId: number;
    invoiceNo?: string;
    payments: InvoicePaymentRow[];
    loading: boolean;
    error?: string | null;
  } | null>(null);

  const [paymentForm, setPaymentForm] = useState<{
    kind: "PAYMENT" | "REFUND";
    amount: string;
    method: string;
    notes: string;
  }>({ kind: "PAYMENT", amount: "", method: "Cash", notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (await apiGet(`/api/manager/invoices/patient/${encodeURIComponent(patientId)}?limit=100`)) as InvoiceRow[];
      setRows(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      setRows([]);
      setError((e as Error)?.message || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openPayments = async (invoice: InvoiceRow) => {
    setPaymentModal({ invoiceId: invoice.id, invoiceNo: invoice.invoice_no, payments: [], loading: true, error: null });
    setPaymentForm({ kind: "PAYMENT", amount: "", method: "Cash", notes: "" });
    try {
      const data = (await apiGet(`/api/manager/invoices/${invoice.id}/payments`)) as InvoicePaymentRow[];
      setPaymentModal({ invoiceId: invoice.id, invoiceNo: invoice.invoice_no, payments: Array.isArray(data) ? data : [], loading: false, error: null });
    } catch (e: unknown) {
      setPaymentModal({ invoiceId: invoice.id, invoiceNo: invoice.invoice_no, payments: [], loading: false, error: (e as Error)?.message || "Failed to load payments" });
    }
  };

  const submitPayment = async () => {
    if (!paymentModal) return;
    const amount = Number(paymentForm.amount || 0);
    if (!Number.isFinite(amount) || amount <= 0) return alert("Enter a valid amount.");
    setPaymentModal((prev) => (prev ? { ...prev, loading: true, error: null } : prev));
    try {
      await apiPost(`/api/manager/invoices/${paymentModal.invoiceId}/payments`, {
        kind: paymentForm.kind,
        amount,
        method: paymentForm.method || null,
        notes: paymentForm.notes?.trim() || null,
      });
      await load();
      const data = (await apiGet(`/api/manager/invoices/${paymentModal.invoiceId}/payments`)) as InvoicePaymentRow[];
      setPaymentModal((prev) => prev ? { ...prev, payments: Array.isArray(data) ? data : [], loading: false } : prev);
      setPaymentForm({ kind: "PAYMENT", amount: "", method: paymentForm.method || "Cash", notes: "" });
    } catch (e: unknown) {
      setPaymentModal((prev) => (prev ? { ...prev, loading: false, error: (e as Error)?.message || "Payment save failed" } : prev));
    }
  };

  const totals = useMemo(() => {
    const finalTotal = rows.reduce((sum, r) => sum + Number(r.final_amount || 0), 0);
    const paidTotal = rows.reduce((sum, r) => sum + Number(r.paid_total || 0), 0);
    const dueTotal = rows.reduce((sum, r) => sum + Number(r.balance_due || 0), 0);
    return { finalTotal, paidTotal, dueTotal };
  }, [rows]);

  return (
    <div className="history-container card">
      <div className="history-header">
        <div>
          <h3>🧾 Reference Invoices</h3>
          <span className="history-sub">Collections reconcile here; revenue reports remain unchanged.</span>
        </div>
        <div className="history-summary">
          <div className="summary-item">
            <span>Total</span>
            <strong>₹{totals.finalTotal.toLocaleString("en-IN")}</strong>
          </div>
          <div className="summary-item success">
            <span>Paid</span>
            <strong>₹{totals.paidTotal.toLocaleString("en-IN")}</strong>
          </div>
          <div className="summary-item danger">
            <span>Due</span>
            <strong>₹{totals.dueTotal.toLocaleString("en-IN")}</strong>
          </div>
        </div>
      </div>

      {error ? <div className="mph-error">⚠️ {error}</div> : null}

      {loading && rows.length === 0 ? (
        <div className="loading-state">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">No invoices found.</div>
      ) : (
        <table className="records-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Invoice</th>
              <th>Final</th>
              <th>Paid</th>
              <th>Due</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="mono">{(r.invoice_date || "").slice(0, 10) || "—"}</td>
                <td className="mono">{r.invoice_no || `#${r.id}`}</td>
                <td className="font-medium">₹{Number(r.final_amount || 0).toLocaleString("en-IN")}</td>
                <td className="font-medium">₹{Number(r.paid_total || 0).toLocaleString("en-IN")}</td>
                <td className={`font-medium ${Number(r.balance_due || 0) > 0 ? "text-red" : "text-green"}`}>
                  ₹{Number(r.balance_due || 0).toLocaleString("en-IN")}
                </td>
                <td>
                  <span className={`status-badge ${(r.status ?? "").toLowerCase()}`}>{r.status || "—"}</span>
                </td>
                <td className="text-right">
                  <div className="mph-actions">
                    <button type="button" className="btn-mini" onClick={() => void openPayments(r)}>
                      Payments
                    </button>
                    <button
                      className="btn-icon-print"
                      type="button"
                      onClick={() => window.open(`/print?type=reference-invoice&invoice=${encodeURIComponent(String(r.invoice_no || r.id))}`, "_blank")}
                      title="Print"
                    >
                      🖨️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {paymentModal ? (
        <div className="mph-modal-overlay" onClick={() => setPaymentModal(null)}>
          <div className="mph-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="mph-modal-head">
              <div className="mph-modal-title">Invoice Payments — {paymentModal.invoiceNo || `#${paymentModal.invoiceId}`}</div>
              <button type="button" className="btn-mini" onClick={() => setPaymentModal(null)}>Close</button>
            </div>
            {paymentModal.error ? <div className="mph-error">⚠️ {paymentModal.error}</div> : null}

            <div className="mph-payform-row">
              <select value={paymentForm.kind} onChange={(e) => setPaymentForm((p) => ({ ...p, kind: e.target.value as "PAYMENT" | "REFUND" }))} disabled={paymentModal.loading}>
                <option value="PAYMENT">Payment</option>
                <option value="REFUND">Refund</option>
              </select>
              <input type="number" min={0} step={0.01} placeholder="Amount" value={paymentForm.amount} onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))} disabled={paymentModal.loading} />
              <input placeholder="Method" value={paymentForm.method} onChange={(e) => setPaymentForm((p) => ({ ...p, method: e.target.value }))} disabled={paymentModal.loading} />
              <input placeholder="Notes" value={paymentForm.notes} onChange={(e) => setPaymentForm((p) => ({ ...p, notes: e.target.value }))} disabled={paymentModal.loading} />
              <button type="button" className="btn-mini btn-pay-balance" onClick={() => void submitPayment()} disabled={paymentModal.loading}>
                {paymentModal.loading ? "Saving..." : "Save"}
              </button>
            </div>

            <div style={{ marginTop: 10 }}>
              <div className="mph-payments-title">Ledger</div>
              {paymentModal.loading && paymentModal.payments.length === 0 ? (
                <div className="text-muted">Loading…</div>
              ) : paymentModal.payments.length === 0 ? (
                <div className="text-muted">No payments</div>
              ) : (
                <table className="mph-payments-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Kind</th>
                      <th>Mode</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentModal.payments.map((p) => (
                      <tr key={p.id}>
                        <td className="mono">{p.created_at ? String(p.created_at).slice(0, 19).replace("T", " ") : "—"}</td>
                        <td className="mono">{p.kind || "PAYMENT"}</td>
                        <td>{p.method || "—"}</td>
                        <td className="text-right">₹{Number(p.amount || 0).toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function PatientPaymentConsole() {
  const [patient, setPatient] = useState<SelectedPatient | null>(null);
  const [tab, setTab] = useState<"bills" | "invoices">("bills");

  const handleSelect = (p: PatientResult) => {
    setPatient({ patient_id: p.patient_id, name: p.name });
    setTab("bills");
  };

  return (
    <div className="records-page">
      <div className="records-toolbar">
        <h3>🔎 Patient Payments</h3>
        {patient && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button type="button" className={tab === "bills" ? "btn-export" : "btn-mini"} onClick={() => setTab("bills")}>
              Bills
            </button>
            <button type="button" className={tab === "invoices" ? "btn-export" : "btn-mini"} onClick={() => setTab("invoices")}>
              Invoices
            </button>
            <button type="button" className="btn-export" onClick={() => setPatient(null)}>
              Clear
            </button>
          </div>
        )}
      </div>

      <div className="records-container">
        <PatientSearch onSelect={handleSelect} />
      </div>

      {patient ? (
        tab === "bills" ? (
          <ManagerPatientHistory patient={patient} />
        ) : (
          <PatientInvoiceLedger patientId={String(patient.patient_id)} />
        )
      ) : (
        <div className="history-container">
          <div className="empty-state">Search and select a patient to see ledger & payments.</div>
        </div>
      )}
    </div>
  );
}
